import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import NewsletterForm from '@/components/base/NewsletterForm';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言！我们会尽快与您联系。');
      form.reset();
      return;
    }

    const data = new URLSearchParams();
    data.append('name', formData.get('name') as string);
    data.append('email', formData.get('email') as string);
    data.append('subject', formData.get('subject') as string);
    data.append('message', formData.get('message') as string);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u002l3pcjqs2fcsjng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试。';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setFormMessage('感谢您的留言！我们会尽快与您联系。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const teamMembers = [
    {
      name: '李明远',
      role: '创始人 & 主编',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20man%20in%20his%20mid%2030s%20wearing%20a%20clean%20white%20shirt%2C%20warm%20confident%20expression%2C%20simple%20studio%20portrait%20with%20soft%20neutral%20background%2C%20editorial%20style%20headshot%20with%20natural%20lighting&width=200&height=200&seq=author-lmy&orientation=squarish',
      bio: '前麦肯锡战略顾问，15年职业发展领域经验',
    },
    {
      name: '王思涵',
      role: '内容总监',
      avatar: 'https://readdy.ai/api/search-image?query=Asian%20woman%20in%20her%20early%2030s%20with%20warm%20smile%2C%20wearing%20a%20simple%20beige%20blouse%2C%20natural%20studio%20portrait%20with%20soft%20lighting%20and%20clean%20cream%20background&width=200&height=200&seq=author-wsh&orientation=squarish',
      bio: '心理学博士，专注于成长型思维与正念研究',
    },
    {
      name: '陈雅文',
      role: '文化专栏主编',
      avatar: 'https://readdy.ai/api/search-image?query=Asian%20woman%20artist%20in%20her%20late%2020s%2C%20warm%20creative%20expression%2C%20wearing%20minimalist%20cream%20attire%2C%20studio%20portrait%20with%20artistic%20soft%20lighting%20and%20neutral%20background&width=200&height=200&seq=author-cyw&orientation=squarish',
      bio: '跨文化研究学者，深耕亚裔身份认同议题',
    },
    {
      name: '赵志强',
      role: '战略顾问',
      avatar: 'https://readdy.ai/api/search-image?query=Mature%20Asian%20man%20in%20his%2040s%2C%20confident%20professional%20demeanor%2C%20wearing%20a%20dark%20navy%20suit%2C%20studio%20headshot%20with%20warm%20lighting%20and%20minimal%20background&width=200&height=200&seq=author-zzq&orientation=squarish',
      bio: '世界500强企业前高管，领导力发展专家',
    },
  ];

  const milestones = [
    { year: '2024', title: '平台创立', desc: '由一群关注亚洲人成长话题的专业人士共同创立，致力于打造系统性成长内容平台。' },
    { year: '2025', title: '内容爆发', desc: '汇聚50+资深作者，发布200+深度文章，月度读者突破5万人，成为亚洲成长领域领先平台。' },
    { year: '2026', title: '生态扩展', desc: '月度读者突破10万，内容覆盖六大核心领域，推出订阅资讯和社区互动功能。' },
  ];

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 亚洲人成长之路',
          description: '了解亚洲人成长之路的品牌故事、核心团队和发展历程。致力于为亚洲人提供系统性的成长指引。',
          url: 'https://snjrh.cn/about',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="py-4">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>
          </div>

          <section className="py-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
              <span className="text-xs font-semibold text-primary-600 uppercase tracking-wider">关于我们</span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-3 mb-5">
                连接亚洲智慧，助力全球成长
              </h1>
              <p className="text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                亚洲人成长之路是一个专注于亚洲人群全面发展的知识平台。我们相信，每一位亚洲人都有着独特的成长路径和无限的潜能。
                我们的使命是汇聚亚洲智慧，提供系统性的成长指引，帮助您在全球化的时代中找到属于自己的方向。
              </p>
            </div>
          </section>

          <section className="py-14 bg-background-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                    <i className="ri-eye-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    成为全球最具影响力的亚洲人成长平台，让每一位有志于全面发展的人都能在这里找到启发和指引。
                  </p>
                </div>

                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className="ri-compass-3-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    系统性输出高质量成长内容，帮助亚洲人群突破文化与职业的双重壁垒，实现个人价值的最大化。
                  </p>
                </div>

                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-4">
                    <i className="ri-heart-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">核心价值观</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    深度、真实、包容——我们坚持输出有深度的内容，分享真实的经验，构建包容的成长社区。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  发展历程
                </h2>
                <p className="text-foreground-600">
                  从创立到成长，每一步都凝聚着团队的初心和读者的信任。
                </p>
              </div>

              <div className="space-y-6">
                {milestones.map((m, i) => (
                  <div key={m.year} className="flex gap-6">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold text-sm whitespace-nowrap">
                        {m.year}
                      </div>
                      {i < milestones.length - 1 && (
                        <div className="w-0.5 flex-1 bg-background-200 mt-2"></div>
                      )}
                    </div>
                    <div className="pb-6">
                      <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">
                        {m.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 bg-background-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  核心团队
                </h2>
                <p className="text-foreground-600">
                  来自不同领域的专业人士，因共同信念而汇聚。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {teamMembers.map((member) => (
                  <div key={member.name} className="bg-background-50 rounded-lg border border-background-200/70 p-5 text-center">
                    <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4">
                      <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-900">{member.name}</h3>
                    <span className="text-xs text-primary-600 font-medium">{member.role}</span>
                    <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{member.bio}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    联系我们
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-6">
                    无论您是有内容合作意向、广告投放需求，还是想成为我们的作者，都欢迎通过以下方式与我们取得联系。
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <span className="block text-sm font-medium text-foreground-900">电子邮箱</span>
                        <span className="text-sm text-foreground-600">contact@snjrh.cn</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <span className="block text-sm font-medium text-foreground-900">办公地址</span>
                        <span className="text-sm text-foreground-600">中国上海市浦东新区世纪大道100号</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <div>
                        <span className="block text-sm font-medium text-foreground-900">微信公众号</span>
                        <span className="text-sm text-foreground-600">亚洲人成长之路</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">
                    发送消息
                  </h3>

                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                    <div className="form-honeypot">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>

                    {formStatus !== 'idle' && (
                      <p
                        className={`text-sm text-center ${
                          formStatus === 'success' ? 'text-accent-700' : 'text-foreground-600'
                        }`}
                      >
                        {formMessage}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          <NewsletterForm />
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}